import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import axios from 'axios';

// Admin token for API calls - set in .env.local as REACT_APP_ADMIN_TOKEN. No hardcoded
// fallback: a misconfigured build should fail admin auth loudly, not silently grant access
// via a value baked into source control.
const ADMIN_TOKEN = process.env.REACT_APP_ADMIN_TOKEN;

// Helper function to get admin headers
const getAdminHeaders = () => ({
  'X-Admin-Token': ADMIN_TOKEN
});

// Async thunks for API calls

/**
 * Fetch all users
 * Backend endpoint: GET /api/admin/users/list
 */
export const fetchAllUsers = createAsyncThunk('adminUsers/fetchAll', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get('/api/admin/users/list', {
      headers: getAdminHeaders()
    });

    if (response.data.status === 'success') {
      return response.data.data.users;
    }
    return rejectWithValue(response.data.message || 'Failed to fetch users');
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

/**
 * Fetch single user details
 * Backend endpoint: GET /api/users/admin/user_info?user_id={user_id}
 */
export const fetchUserById = createAsyncThunk('adminUsers/fetchById', async (userId, { rejectWithValue }) => {
  try {
    const response = await axios.get(`/api/users/admin/user_info?user_id=${userId}`, {
      headers: getAdminHeaders()
    });

    if (response.data.status === 'success') {
      return response.data.data;
    }
    return rejectWithValue('Failed to fetch user');
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

/**
 * Create new user with subscription
 * Backend endpoint: POST /api/users/admin/create_user
 */
export const createUser = createAsyncThunk('adminUsers/create', async (userData, { rejectWithValue }) => {
  try {
    const response = await axios.post('/api/users/admin/create_user', userData, {
      headers: getAdminHeaders()
    });

    if (response.data.status === 'success') {
      return response.data.data.user;
    }
    return rejectWithValue(response.data.message || 'Failed to create user');
  } catch (error) {
    // Handle both 'errors' (validation) and 'message' (general errors)
    const errorMessage = error.response?.data?.errors
      ? JSON.stringify(error.response.data.errors)
      : error.response?.data?.message || error.message;
    return rejectWithValue(errorMessage);
  }
});

/**
 * Update user details
 * Backend endpoint: PUT /api/users/admin/update_user/{user_id}
 */
export const updateUser = createAsyncThunk('adminUsers/update', async ({ userId, userData }, { rejectWithValue }) => {
  try {
    const response = await axios.put(`/api/users/admin/update_user/${userId}`, userData, {
      headers: getAdminHeaders()
    });

    if (response.data.status === 'success') {
      return { userId, ...response.data.data.user };
    }
    return rejectWithValue(response.data.message || 'Failed to update user');
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

/**
 * Delete user
 * Backend endpoint: DELETE /api/users/admin/delete_user/{user_id}
 */
export const deleteUser = createAsyncThunk('adminUsers/delete', async (userId, { rejectWithValue }) => {
  try {
    const response = await axios.delete(`/api/users/admin/delete_user/${userId}`, {
      headers: getAdminHeaders()
    });

    if (response.data.status === 'success') {
      return userId;
    }
    return rejectWithValue(response.data.message || 'Failed to delete user');
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

/**
 * Update user subscription
 * Backend endpoint: PATCH /api/admin/users/{user_id}/subscription
 */
export const updateUserSubscription = createAsyncThunk(
  'adminUsers/updateSubscription',
  async ({ userId, subscriptionData }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`/api/admin/users/${userId}/subscription`, subscriptionData, { headers: getAdminHeaders() });

      if (response.data.status === 'success') {
        return { userId, ...response.data.data.subscription };
      }
      return rejectWithValue(response.data.message || 'Failed to update subscription');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  users: [],
  selectedUser: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  searchTerm: '',
  filterPlan: 'all', // 'all' | 'FREE' | 'TRIAL' | 'SUBSCRIBER'
  filterStatus: 'all' // 'all' | 'ACTIVE' | 'CANCELLED'
};

const adminUsersSlice = createSlice({
  name: 'adminUsers',
  initialState,
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setFilterPlan: (state, action) => {
      state.filterPlan = action.payload;
    },
    setFilterStatus: (state, action) => {
      state.filterStatus = action.payload;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all users
      .addCase(fetchAllUsers.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.users = action.payload;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Fetch user by ID
      .addCase(fetchUserById.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.selectedUser = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Create user
      .addCase(createUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.users.push(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Update user
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex((u) => u.user_id === action.payload.userId);
        if (index !== -1) {
          state.users[index] = { ...state.users[index], ...action.payload };
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Delete user
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((u) => u.user_id !== action.payload);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Update subscription
      .addCase(updateUserSubscription.fulfilled, (state, action) => {
        const index = state.users.findIndex((u) => u.user_id === action.payload.userId);
        if (index !== -1 && state.users[index].subscription) {
          state.users[index].subscription = {
            ...state.users[index].subscription,
            ...action.payload
          };
        }
      });
  }
});

export const { setSearchTerm, setFilterPlan, setFilterStatus, clearSelectedUser, clearError } = adminUsersSlice.actions;

// Selectors
export const selectAllUsers = (state) => state.adminUsers.users;
export const selectFilteredUsers = createSelector(
  [
    (state) => state.adminUsers.users,
    (state) => state.adminUsers.searchTerm,
    (state) => state.adminUsers.filterPlan,
    (state) => state.adminUsers.filterStatus
  ],
  (users, searchTerm, filterPlan, filterStatus) => {
    return users.filter((user) => {
      const matchesSearch =
        searchTerm === '' ||
        user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.company && user.company.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesPlan = filterPlan === 'all' || user.subscription?.subscription_plan === filterPlan;

      const matchesStatus = filterStatus === 'all' || user.subscription?.status === filterStatus;

      return matchesSearch && matchesPlan && matchesStatus;
    });
  }
);

export default adminUsersSlice.reducer;
